import { useState, useEffect } from 'react';
import { Button, Card, Alert, Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import Icon from '../../../../components/Icon/Icon';
import WhatsAppAgentsDataTable from './WhatsAppAgentsDataTable';
import AddEditAgentModal from '../modals/AddEditAgentModal';
import { whatsappAgentAPI } from '../../../../helpers/whatsappApiHandler';
import contactEmptyMascot from '../../../../assets/contact-empty-mascot.png';

const WhatsAppAgentsTabContent = ({ wabaId }) => {
  const [agentsData, setAgentsData] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Get access token from Redux store
  const accessToken = useSelector(state => state.auth.access_token?.token);

  // Load agents data on component mount
  useEffect(() => {
    if (accessToken) {
      loadAgentsData();
    }
  }, [accessToken]);

  const loadAgentsData = async () => {
    setLoadingData(true);
    setError('');

    try {
      const response = await whatsappAgentAPI.getManagerAgents(accessToken);

      if (response.success) {
        // Transform the data to match the expected format
        const transformedData = response.data.agents.map(agent => ({
          id: agent.agent_id,
          agent_id: agent.agent_id,
          name: agent.name,
          email: agent.email,
          lastActive: agent.last_active,
          role: agent.role,
          status: agent.status,
          assignedAt: agent.assigned_at
        }));
        setAgentsData(transformedData);
      } else {
        setError(response.message || 'Failed to load agents data');
      }
    } catch (err) {
      console.error('Error loading agents:', err);
      setError('Failed to load agents data');
    } finally {
      setLoadingData(false);
    }
  };

  const handleAddAgent = () => {
    setShowAddModal(true);
  };

  const handleAgentAdded = async (email) => {
    try {
      const response = await whatsappAgentAPI.inviteAgent(email, wabaId, accessToken);

      if (response.success) {
        setSuccess('Agent invitation sent successfully!');
        // Reload the agents data to show the new pending invitation
        await loadAgentsData();

        // Clear success message after 5 seconds
        setTimeout(() => setSuccess(''), 5000);
      } else {
        throw new Error(response.message || 'Failed to send invitation');
      }
    } catch (err) {
      console.error('Error inviting agent:', err);
      throw err; // Re-throw to be handled by the modal
    }
  };

  const renderAgentsHeader = () => (
    <div style={{
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      alignSelf: 'stretch',
      gap: '46px',
      padding: '20px 32px',
      borderRadius: '0px 20px 0px 0px',
      backgroundColor: '#FFFFFF'
    }}>
      {/* Left side - Description */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        flex: 1
      }}>
        <p style={{
          fontFamily: 'Poppins',
          fontWeight: '400',
          fontSize: '12px',
          lineHeight: '1.5em',
          color: '#737C8A',
          margin: '0',
          whiteSpace: 'nowrap'
        }}>
          Add, remove, or update agents who handle customer conversations on your WhatsApp Business account.
        </p>
      </div>

      {/* Right side - Add Agent Button */}
      <div style={{
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: '16px',
        width: '782px'
      }}>
        <Button
          onClick={handleAddAgent}
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 18px',
            backgroundColor: '#0BA9E0',
            border: 'none',
            borderRadius: '4px',
            boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
            fontFamily: 'Poppins',
            fontWeight: '500',
            fontSize: '13px',
            lineHeight: '1.5em',
            color: '#FFFFFF'
          }}
        >
          <Icon
            name="add"
            color="#FFFFFF"
            width={17}
            height={17}
          />
          Add Agent
        </Button>
      </div>
    </div>
  );

  // Show loading state
  if (loadingData && agentsData.length === 0) {
    return (
      <div style={{
        backgroundColor: 'white',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        borderRadius: '0px 20px 20px 20px',
        margin: '0 40px 40px 40px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px'
      }}>
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2 text-muted">Loading agents...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      backgroundColor: 'white',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      borderRadius: '0px 20px 20px 20px',
      margin: '0 40px 40px 40px',
      maxWidth: 'calc(100vw - 80px)', // Ensure it doesn't exceed viewport width minus margins
      overflow: 'hidden' // Prevent content from overflowing
    }}>
      <div style={{ padding: '0' }}>
        {renderAgentsHeader()}

        {/* Success/Error Messages */}
        {success && (
          <div style={{ padding: '0 32px 16px 32px' }}>
            <Alert variant="success" dismissible onClose={() => setSuccess('')}>
              {success}
            </Alert>
          </div>
        )}

        {error && (
          <div style={{ padding: '0 32px 16px 32px' }}>
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              <strong>Error:</strong> {error}
              <Button
                variant="outline-danger"
                size="sm"
                className="ms-2"
                onClick={loadAgentsData}
              >
                Retry
              </Button>
            </Alert>
          </div>
        )}

        <div style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '0px 0px 20px 20px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'stretch',
          gap: '20px',
          width: '100%', // Ensure full width usage within container
          overflowX: 'auto' // Allow horizontal scrolling if needed
        }}>
          <div style={{
            width: '100%',
            overflowX: 'auto' // Allow table to scroll horizontally if needed
          }}>
            <WhatsAppAgentsDataTable
              data={agentsData}
              loadingData={loadingData}
              onRefresh={loadAgentsData}
              accessToken={accessToken}
            />
          </div>
        </div>
      </div>

      {/* Add Agent Modal */}
      <AddEditAgentModal
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        onAgentAdded={handleAgentAdded}
      />
    </div>
  );
};

export default WhatsAppAgentsTabContent;
